import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import {
  X,
  TableCellsMerge,
  Sheet,
  FileSliders,
  ChartNoAxesCombined,
  GitGraph,
  Wrench
} from "lucide-react";
import AssetDetails from "./AssetDetails";
import ResultsContent from "./ResultContent";
import ViewLineage from "./ViewLineage";
import ContractDetailsContent from "./ContractDetailsContent";
import LineageTransformationContent from "./LineageTransformationContent";
import heartLoading from "../../../assests/heartLoading.gif";
import { UsageTrend } from "../Domain/UsageTrend";
import KpiLineage from "../Domain/KpiLineage";

const AssetDetailsModal = ({
  showAssetModal,
  selectedAssetForView,
  assetLoading,
  assetData,
  setShowAssetModal,
  setSelectedAssetForView,
  setAssetData,
  searchQuery
}) => {
  const [activeTab, setActiveTab] = useState("Data Table");

  const [profilingResults, setProfilingResults] = useState(null);
  const [profilingLoading, setProfilingLoading] = useState(false);
  const [dqResults, setDqResults] = useState(null);
  const [dqLoading, setDqLoading] = useState(false);
  const [activeResultTab, setActiveResultTab] = useState("profile");

  const [contractDetails, setContractDetails] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const tabsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [usageTrendData, setUsageTrendData] = useState(null);
  const [usageTrendLoading, setUsageTrendLoading] = useState(false);
  const [kpiLineageData, setKpiLineageData] = useState(null);
  const [kpiLineageLoading, setKpiLineageLoading] = useState(false);
  const [kpiLineageFetched, setKpiLineageFetched] = useState(false);
  const [businessSortBy, setBusinessSortBy] = useState("");
  const [businessSortOrder, setBusinessSortOrder] = useState("asc");
  const [businessColumnSearch, setBusinessColumnSearch] = useState({});
  const [businessPage, setBusinessPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const checkScroll = () => {
    const el = tabsRef.current;
    if (!el) return;

    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };

  const scrollTabs = (direction) => {
    const el = tabsRef.current;
    if (!el) return;

    el.scrollBy({
      left: direction === "left" ? -250 : 250,
      behavior: "smooth",
    });

    setTimeout(checkScroll, 300);
  };
  const handleBusinessSortChange = (key, order) => {
    setBusinessSortBy(key);
    setBusinessSortOrder(order);
  };

  const handleBusinessSearchChange = (key, value) => {
    setBusinessColumnSearch(value ? { [key]: value } : {});
    setBusinessPage(1);
  };

  const fetchKpiLineage = async () => {
    if (!selectedAssetForView?.name) return;
    try {
      setKpiLineageLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/kpi-lineage?asset_name=${selectedAssetForView.name}`
      );
      setKpiLineageData(response.data);
    } catch {
      setKpiLineageData(null);
    } finally {
      setKpiLineageLoading(false);
      setKpiLineageFetched(true);
    }
  };

  const fetchUsageTrend = async () => {
    try {
      setUsageTrendLoading(true);
      const params = {
        page: businessPage,
        page_size: 25,
        ...(businessSortBy && { sort_by: businessSortBy, sort_order: businessSortOrder }),
        ...(Object.keys(businessColumnSearch)[0] && {
          search_by: Object.keys(businessColumnSearch)[0],
          search_value: Object.values(businessColumnSearch)[0],
        }),
      };
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/data_insights/${selectedAssetForView?.id}`,
        { params }
      );
      if (response.status === 200) setUsageTrendData(response.data);
    } catch {
      setUsageTrendData({});
    } finally {
      setUsageTrendLoading(false);
    }
  };

  useEffect(() => {
    const activeKey = Object.keys(businessColumnSearch)[0];
    const value = activeKey ? businessColumnSearch[activeKey] : "";
    const timer = setTimeout(() => setDebouncedSearch(value), 500);
    return () => clearTimeout(timer);
  }, [businessColumnSearch]);

  useEffect(() => {
    if (activeTab === "Usage Trend") fetchUsageTrend();
  }, [activeTab, businessSortBy, businessSortOrder, debouncedSearch, businessPage]);

  useEffect(() => {
    if (activeTab === "KPI Lineage") fetchKpiLineage();
  }, [activeTab]);

  useEffect(() => {
    if (!showAssetModal) return;
    const el = tabsRef.current;
    if (!el) return;

    setTimeout(checkScroll, 100);

    const resizeObserver = new ResizeObserver(() => {
      checkScroll();
    });
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [showAssetModal]);

  const fetchContractDetails = async (dataAssetId) => {
    if (!dataAssetId) return null;
    try {
      setContractLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/view-contracts/${dataAssetId}`
      );
      if (response.data && response.data.status === "success") {
        setContractDetails(response.data.data);
        return response.data.data;
      } else {
        setContractDetails(null);
        return null;
      }
    } catch (err) {
      console.error("Error fetching contract details:", err);
      setContractDetails(null);
      return null;
    } finally {
      setContractLoading(false);
    }
  };

  const fetchProfilingAndDQResults = async () => {
    if (!selectedAssetForView) return;
    const productId = selectedAssetForView.productId;
    const dataAssetId = selectedAssetForView.id;
    if (!productId) return;

    setProfilingLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/profiling_results/${productId}`
      );
      if (response.data && response.data.profiling_results) {
        setProfilingResults(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch profiling results:", err);
    } finally {
      setProfilingLoading(false);
    }

    if (dataAssetId) {
      setDqLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/runQuality/ruleStatus/${productId}`
        );
        if (response.data && Array.isArray(response.data)) {
          const matchingAsset = response.data.find(
            (asset) => asset.data_asset_id === dataAssetId
          );
          if (matchingAsset) setDqResults(matchingAsset);
        }
      } catch (err) {
        console.error("Failed to fetch DQ results:", err);
      } finally {
        setDqLoading(false);
      }
    }
  };

  useEffect(() => {
    if (showAssetModal && selectedAssetForView) {
      if ((activeTab === "Profiling Result" || activeTab === "DQResult") && !profilingResults && !dqResults) {
        fetchProfilingAndDQResults();
      }
      if (activeTab === "Data Configuration" && !contractDetails) {
        fetchContractDetails(selectedAssetForView.id);
      }
    }
  }, [showAssetModal, selectedAssetForView, activeTab]);


  if (!showAssetModal) return null;




  const handleClose = () => {
    setShowAssetModal(false);
    setSelectedAssetForView(null);
    setAssetData(null);
    setActiveTab("Data Table");
    setProfilingResults(null);
    setDqResults(null);
    setContractDetails(null);
    setActiveResultTab("profile");
    setKpiLineageData(null);
    setKpiLineageLoading(false);
    setKpiLineageFetched(false);
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "Profiling Result" && selectedAssetForView && !profilingResults) {
      fetchProfilingAndDQResults();
    }
    if (tabId === "Data Configuration" && selectedAssetForView && !contractDetails) {
      fetchContractDetails(selectedAssetForView.id);
    }
  };

  const tabs = [
    { id: "Data Table", label: "Assets Table", icon: TableCellsMerge },
    { id: "Data Configuration", label: "Data Contract", icon: Sheet },
    { id: "Profiling Result", label: "Profiling Result", icon: FileSliders },
    { id: "DQResult", label: "Data Quality Result", icon: FileSliders },
    { id: "Remediation Actions", label: "Remediation Actions", icon: Wrench },
    { id: "Technical Lineage", label: "Technical Lineage", icon: GitGraph },
    { id: "KPI Lineage", label: "KPI Lineage", icon: GitGraph },
    { id: "Transformation Lineage", label: "Transformation Lineage", icon: GitGraph },
    { id: "Usage Trend", label: "Usage Trend", icon: ChartNoAxesCombined },
  ];

  return (
    <div
      className="fixed  inset-0 w-[100%] h-[100%] bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg w-[1230px] h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header - always visible */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Asset Details</h2>
            {selectedAssetForView && (
              <p className="text-sm text-gray-600 mt-1">
                {selectedAssetForView.name}
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs - always visible */}
        <div className="border-b border-gray-200 px-6 relative">

          {/* LEFT ARROW */}

          {canScrollLeft && (
            <button

              onClick={() => scrollTabs("left")}

              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-[#2DD4BF] shadow-md rounded-full p-1 hover:bg-teal-500"
            >
              <ChevronLeft size={18} className="text-white" />
            </button>

          )}

          {canScrollRight && (
            <button

              onClick={() => scrollTabs("right")}

              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-[#2DD4BF] shadow-md rounded-full p-1 hover:bg-teal-500"
            >
              <ChevronRight size={18} className="text-white" />
            </button>

          )}


          {/* Tabs */}
          <nav

            ref={tabsRef}

            onScroll={checkScroll}

            className="flex space-x-8 overflow-x-auto pb-2 scroll-smooth"
          >

            {tabs.map((tab) => (
              <button

                key={tab.id}

                onClick={() => handleTabClick(tab.id)}

                className={`whitespace-nowrap flex-shrink-0 flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors

          ${activeTab === tab.id

                    ? "border-[#bb1ccc] text-[#bb1ccc]"

                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"

                  }`}
              >
                <tab.icon size={18} />

                {tab.label}
              </button>

            ))}
          </nav>

        </div>



        {/* Tab Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-180px)]">

          {/* Loader inside content area */}
          {assetLoading && (
            <div className="flex items-center justify-center h-[300px]">
              <div className="flex flex-col items-center space-y-3">
                <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
                <span className="text-gray-700 text-[20px] text-center">
                  Loading asset details...
                </span>
              </div>
            </div>
          )}

          {!assetLoading && activeTab === "Data Table" && (
            <AssetDetails assetData={assetData} searchQuery={searchQuery} height={350} />
          )}

          {!assetLoading && activeTab === "Data Configuration" && (
            <ContractDetailsContent
              selectedContractForView={selectedAssetForView}
              contractDetails={contractDetails}
              contractLoading={contractLoading}
            />
          )}

          {!assetLoading && activeTab === "Profiling Result" && (
            <ResultsContent
              activeResultTab={activeResultTab}
              setActiveResultTab={setActiveResultTab}
              profilingLoading={profilingLoading}
              profilingResults={profilingResults}
              dqLoading={dqLoading}
              dqResults={dqResults}
              assetName={selectedAssetForView?.name}
              showTabs={false}
            />
          )}

          {!assetLoading && activeTab === "DQResult" && (
            <ResultsContent
              activeResultTab="quality"
              setActiveResultTab={setActiveResultTab}
              profilingLoading={false}
              profilingResults={null}
              dqLoading={dqLoading}
              dqResults={dqResults}
              assetName={selectedAssetForView?.name}
              showTabs={false}
            />
          )}
          {!assetLoading && activeTab === "Remediation Actions" && (
            <div className="flex flex-col items-center justify-center h-[300px] gap-2 text-gray-400">
              <h3 className="text-lg font-semibold">Coming Soon</h3>
              <p className="text-sm">Remediation Actions will be available shortly.</p>
            </div>
          )}
          {!assetLoading && activeTab === "Technical Lineage" && (
            <div className="h-[calc(90vh-300px)]">
              <ViewLineage contractData={selectedAssetForView} />
            </div>
          )}

          {!assetLoading && activeTab === "KPI Lineage" && (
            <KpiLineage
              data={kpiLineageData}
              loading={kpiLineageLoading}
              fetched={kpiLineageFetched}
              assetName={selectedAssetForView?.name}
            />
          )}

          {!assetLoading && activeTab === "Transformation Lineage" && (
            <LineageTransformationContent
              assetName={selectedAssetForView?.name || selectedAssetForView?.asset_name || ""}
            />
          )}

          {!assetLoading && activeTab === "Usage Trend" && (
            <UsageTrend
              usageData={usageTrendData}
              accumulatedUsers={usageTrendData?.business_users || []}
              loading={usageTrendLoading}
              sortBy={businessSortBy}
              sortOrder={businessSortOrder}
              columnSearch={businessColumnSearch}
              onSortChange={handleBusinessSortChange}
              onSearchChange={handleBusinessSearchChange}
              handleBusinessPageChange={setBusinessPage}
            />
          )}

        </div>

      </div>
    </div>
  );
};

export default AssetDetailsModal;